import { ScoreStickerV2, Skeleton, Text, TextColor, TextVariant } from '@vizit/atoms';
import { AssetCountV2, AssetMixV2, AssetOrderV2, Heading, HeadingVariant, ScoreMetricCell } from '@vizit/molecules';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { cardClasses, containerClasses, mainScoreClasses, metricsClasses } from './styles';
import type { ProductPagePerformanceProps } from './types';

/**
 * Product page performance section displaying the main score
 * alongside metric cards for avg. assets score, hero asset score,
 * asset mix, asset count, and asset order.
 *
 * @example
 * <ProductPagePerformance
 *   pageScore={38}
 *   avgAssetsScore={{ label: 'CAROUSEL SCORE', score: 32 }}
 *   heroAssetScore={{ label: 'HERO ASSET SCORE', score: 32, scoreDiff: 5 }}
 *   assetMix={{ missing: 2, required: 7 }}
 *   assetCount={{ count: 8, optimal: 7 }}
 *   isAssetOrderSorted={false}
 * />
 */
const ProductPagePerformance = React.forwardRef<HTMLDivElement, ProductPagePerformanceProps>((props, forwardedRef) => {
  const {
    pageScore,
    pageScoreDiff,
    avgAssetsScore,
    heroAssetScore,
    assetMix,
    assetCount,
    isAssetOrderSorted,
    isLoading = false,
    onReorder,
    highlightedSection,
    className,
    ...other
  } = props;

  const isCarouselHighlighted = highlightedSection === 'carousel-scores';
  const isHeroHighlighted = highlightedSection === 'hero-score';
  const isAssetMixHighlighted = highlightedSection === 'asset-mix';
  const hasHighlight = highlightedSection != null;

  return (
    <div
      ref={forwardedRef}
      className={cx(containerClasses(), className)}
      data-testid="product-page-performance"
      {...other}
    >
      <Heading variant={HeadingVariant.H6}>Product page performance</Heading>

      <div className={cx(cardClasses(), hasHighlight && 'border-transparent bg-transparent')}>
        <div className={cx(mainScoreClasses(), hasHighlight && 'border-r-transparent')}>
          {isLoading ? (
            <Skeleton variant="square" className="h-5 w-32" />
          ) : (
            <Text variant={TextVariant.MonoXs} color={TextColor.Quaternary} showEllipsis={false}>
              PRODUCT PAGE SCORE
            </Text>
          )}
          <ScoreStickerV2 score={pageScore} scoreDiff={pageScoreDiff} isLoading={isLoading} />
        </div>

        <div className={cx(metricsClasses(), hasHighlight && 'divide-transparent')}>
          <ScoreMetricCell
            label={avgAssetsScore.label}
            score={avgAssetsScore.score}
            impactLevel="high"
            scoreDiff={avgAssetsScore.scoreDiff}
            isLoading={isLoading}
            isFocused={isCarouselHighlighted}
            isDisabled={hasHighlight && !isCarouselHighlighted}
            className="flex-1"
          />
          <ScoreMetricCell
            label={heroAssetScore.label}
            score={heroAssetScore.score}
            impactLevel="medium"
            scoreDiff={heroAssetScore.scoreDiff}
            isLoading={isLoading}
            isFocused={isHeroHighlighted}
            isDisabled={hasHighlight && !isHeroHighlighted}
            className="flex-1"
          />
          <AssetMixV2
            missing={assetMix.missing}
            required={assetMix.required}
            impactLevel="medium"
            isLoading={isLoading}
            isFocused={isAssetMixHighlighted}
            isDisabled={hasHighlight && !isAssetMixHighlighted}
            className="flex-1"
          />
          <AssetCountV2
            count={assetCount.count}
            optimal={assetCount.optimal}
            impactLevel="low"
            isLoading={isLoading}
            isDisabled={hasHighlight}
            className="flex-1"
          />
          <AssetOrderV2
            isSorted={isAssetOrderSorted}
            isLoading={isLoading}
            isDisabled={hasHighlight}
            onReorder={onReorder}
            className="flex-1"
          />
        </div>
      </div>
    </div>
  );
});

ProductPagePerformance.displayName = 'ProductPagePerformance';

export { ProductPagePerformance };
export type { ProductPagePerformanceProps };
